// The tenant kinds. Declared here rather than in the schema so the frontend can
// import the union instead of hand-mirroring it — @repo/db is Node-only. Same
// arrangement TRIP_STATUSES already has with packages/db/src/schema/trips.ts,
// which builds its pg enum from the constant.
export const TENANT_TYPES = ['standard', 'carrier', 'headcount', 'contractor'] as const
export type TenantType = (typeof TENANT_TYPES)[number]

export const RESOURCES = [
  'quotes',
  'contractors',
  'users_management',
  'driver_onboarding',
  'driver_documents',
  'driver_invitations',
  'driver_insurance',
  'audit_log',
  'contractor_companies',
  'projects',
  'own_quotes',
  'own_onboarding',
  'materials',
  'materials_management',
  'facilities',
  'facilities_management',
  'orders',
  'orders_quick_create',
  // Admin-only override for editing costs after the order locks (PET-440).
  'orders_lock_override',
  'dispatches',
  'imports',
  // Dispatch pipeline (PET-336)
  'partnerships',
  'trucks',
  'own_trucks',
  'operational_drivers',
  // PET-616: fleet-level docs uploaded on-behalf by a carrier owner for their
  // own drivers and trucks (separate from `driver_documents` which is the
  // office-side driver-onboarding review surface).
  'fleet_documents',
  'dispatch_requests',
  'trips',
  'tickets',
  // PET-424: separate review permissions so the dispatcher gate and the
  // billing override can be assigned independently. The base `tickets:update`
  // perm covers driver/reviewer field edits; review_dispatcher and
  // review_billing gate the two approval stages.
  'tickets_review_dispatcher',
  'tickets_review_billing',
  // Vendors (PET-440)
  'vendors',
  // GPS Tracking (PET-362)
  'gps_tracking',
  // Company settings (PET-333)
  'company',
  // Roles & permissions management (PET-334)
  'roles_management',
  // Geofences (PET-423) — controls editing custom geofence overrides on sites
  'geofences',
  // Hourly time clock (PET-407) — drivers run the clock; dispatcher / billing
  // append delta-row adjustments.
  'time_clock',
  // Carrier onboarding (PET-604) — office invites a carrier company, reviews
  // its documents, approves to provision a new carrier tenant + owner login.
  'carrier_onboarding',
  // Headcount / broker onboarding (PET-659 PR4) — office invites a broker
  // company, reviews its documents, approves to provision a new headcount
  // tenant + owner login.
  'headcount_onboarding',
  // Standard-driver onboarding (PET-659 PR6) — office invites a driver
  // (owner-operator or company driver) to onboard against a standard tenant.
  // Cutover from the legacy `/driver-onboarding` router happens in PR8.
  'standard_driver_onboarding',
  // Broker lites (PET-663) — passive headcount tenants an office creates
  // in-app (no login); `broker_lites_management` gates the admin page.
  'broker_lites',
  'broker_lites_management',
  // Versioned terms-of-service (PET-733) — read the version history and
  // publish a new version (which re-gates every user in every app).
  'terms',
  // Supervisors (PET-2999) — office side. Invite a customer's people onto a
  // project and assign them to orders.
  'project_supervisors',
  // Supervisors (PET-2999) — office side. Read the signature evidence on a
  // ticket and resolve a reconciliation exception.
  'supervisor_signatures',
  // Supervisors (PET-2999) — contractor side. One resource per width of read,
  // so the scoping helper branches on the grant it was handed and never on the
  // caller's role slug:
  //   own_company_projects  — every project the company is on (master)
  //   own_assigned_projects — projects I hold a membership on, in full (supervisor)
  //   own_assigned_orders   — only the orders I am individually assigned (member)
  // The helper takes the widest grant the caller holds.
  'own_company_projects',
  'own_assigned_projects',
  'own_assigned_orders',
  // Supervisors (PET-2999) — contractor side. The master's roster management.
  // Held by every contractor role's template is NOT the intent: only the master
  // carries it.
  'own_company_roster',
  // Supervisors (PET-2999) — both sides of the QR exchange. The supervisor
  // opens a signature session, the driver answers it; each submits one receipt.
  'own_signatures',
  // FMCSA carrier lookups read a cache of the public federal registry, owned by
  // no tenant, so they get a resource of their own rather than an `own_*` one.
  'fmcsa_lookup',
  // PET-3887: the Emblem desk — the insurance agency's own staff working the
  // quote requests, certificates and vehicle specs an office routes to them,
  // signed in as themselves rather than through an emailed link.
  'insurance_desk',
] as const

export type Resource = (typeof RESOURCES)[number]

export const ACTIONS = ['create', 'read', 'update', 'delete', 'start_stop', 'adjust'] as const
export type Action = (typeof ACTIONS)[number]

export type PermissionEntry = { resource: Resource; action: Action }

// The standard office role set. Real offices predate tenant provisioning and
// came from the retired demo seed or a DB dump; the only code path that
// provisions a fresh one is the e2e fixtures router (`E2E-02`).
export const STANDARD_TENANT_SYSTEM_ROLES = [
  { slug: 'driver', name: 'Driver' },
  { slug: 'contractor', name: 'Contractor' },
  { slug: 'salesperson', name: 'Salesperson' },
  { slug: 'billing', name: 'Billing' },
  { slug: 'admin', name: 'Admin' },
  { slug: 'emblem', name: 'Emblem' },
] as const

export type StandardRoleSlug = (typeof STANDARD_TENANT_SYSTEM_ROLES)[number]['slug']

// Carrier tenants only see the carrier-side workflow — drivers, trucks,
// dispatches they receive from a parent office — so they don't need the
// sales/billing roles. `carrier_admin` is included so the carrier can promote
// a trusted internal user to full access on their own tenant without leaking
// unused sales permissions the global `admin` role would carry.
export const CARRIER_TENANT_SYSTEM_ROLES = [
  { slug: 'carrier_owner', name: 'Carrier Owner' },
  { slug: 'carrier_owner_operator', name: 'Carrier Owner Operator' },
  { slug: 'carrier_driver', name: 'Carrier Driver' },
  { slug: 'carrier_admin', name: 'Carrier Admin' },
] as const

export type CarrierRoleSlug = (typeof CARRIER_TENANT_SYSTEM_ROLES)[number]['slug']

// Headcount / broker tenants — the tenant runs a brokerage that dispatches
// hauling work to carrier partners. `headcount_owner` is the invited user
// created on approval; `headcount_admin` mirrors the carrier pattern so the
// owner can promote a trusted internal user. Product will refine the
// permission set later; today these roles carry the same base permissions
// as their carrier counterparts.
export const HEADCOUNT_TENANT_SYSTEM_ROLES = [
  { slug: 'headcount_owner', name: 'Headcount Owner' },
  { slug: 'headcount_admin', name: 'Headcount Admin' },
] as const

export type HeadcountRoleSlug = (typeof HEADCOUNT_TENANT_SYSTEM_ROLES)[number]['slug']

// Contractor (customer) tenants — the construction company a quote is sent to
// and whose people sign for deliveries on site (PET-2999). The tenant IS the
// company, so its sub-roles are ordinary roles rather than a flag on a profile:
// one role per tenant (AUTH-02) only made "a master who also supervises a site"
// inexpressible while the tenant was the office.
//
// `contractor_master`   — the verified principal. Runs the company roster and
//                         sees every project the company is on, at every office.
// `contractor_supervisor` — sees, in full, the projects they are attached to.
// `contractor_member`   — sees only the orders they are individually assigned,
//                         and deliberately holds no `own_quotes`: a site
//                         employee has no business reading pricing.
export const CONTRACTOR_TENANT_SYSTEM_ROLES = [
  { slug: 'contractor_master', name: 'Contractor Master' },
  { slug: 'contractor_supervisor', name: 'Supervisor' },
  { slug: 'contractor_member', name: 'Site Member' },
] as const

export type ContractorRoleSlug = (typeof CONTRACTOR_TENANT_SYSTEM_ROLES)[number]['slug']

// Which roles may authenticate into the contractor portal and the supervisor
// app. Distinct from PORTAL_ONLY_ROLE_SLUGS the same way DRIVER_PORTAL_ROLE_SLUGS
// is: this answers "may they sign in here", that answers "must they be kept out
// of the office app".
export const CONTRACTOR_PORTAL_ROLE_SLUGS: readonly ContractorRoleSlug[] =
  CONTRACTOR_TENANT_SYSTEM_ROLES.map((role) => role.slug)

// Which of those may sign in to the **web** portal (PET-3001, `SUP-22`).
//
// `contractor_member` is deliberately absent. It holds `own_assigned_orders`
// and `own_signatures` and no `own_quotes`, so it would authenticate and then
// 403 on `/quotes` — the portal's only page — landing on the error boundary
// rather than on an explanation. It is an app role, and the supervisor app
// signs in through its own router with the full set above.
//
// Narrower than CONTRACTOR_PORTAL_ROLE_SLUGS on purpose: "may they sign in to
// this surface" and "is this a contractor-tenant role" are different questions,
// and every other consumer here wants the second one.
export const CONTRACTOR_WEB_PORTAL_ROLE_SLUGS: readonly ContractorRoleSlug[] = [
  'contractor_master',
  'contractor_supervisor',
]

// Roles that belong to a dedicated portal app (driver / contractor) rather than
// the office/admin app. The office login must never bind a session to a tenant
// carrying one of these roles when the user also holds an office membership, and
// the office app both hides those tenants from its account switcher and, as a
// last resort, ejects a portal-only session to the matching portal — resolved
// through `portalKindForRole` below, so there is no second copy to keep in step.
//
// Distinct from DRIVER_PORTAL_ROLE_SLUGS (which roles may authenticate into the
// driver portal): carrier_driver is portal-only (it carries driver permissions
// only) and so is included, but carrier_owner_operator is intentionally excluded
// because it also carries a carrier office workspace.
// PET-2999 replaced the single `contractor` slug with the three contractor-tenant
// roles. The slug is kept in the list so that a tenant still holding a legacy
// membership (one the backfill has not moved yet) stays out of the office app.
export const PORTAL_ONLY_ROLE_SLUGS = [
  'driver',
  'carrier_driver',
  'contractor',
  ...CONTRACTOR_PORTAL_ROLE_SLUGS,
] as const

// Which roles may authenticate into the driver portal and the driver app.
// Lived in apps/backend until PET-2999; here so the frontend can resolve a
// role's portal without a second hand-written copy. `carrier_owner_operator` is
// deliberately absent from PORTAL_ONLY_ROLE_SLUGS below — it also carries a
// carrier office workspace (AUTH-07).
export const DRIVER_PORTAL_ROLE_SLUGS = [
  'driver',
  'carrier_driver',
  'carrier_owner_operator',
] as const

const DRIVER_PORTAL_ROLE_SET = new Set<string>(DRIVER_PORTAL_ROLE_SLUGS)
const CONTRACTOR_PORTAL_ROLE_SET = new Set<string>([
  // The legacy office-bound role, until the SUP-14 backfill moves it.
  'contractor',
  ...CONTRACTOR_PORTAL_ROLE_SLUGS,
])

/**
 * Which portal a role belongs to, or `null` for an office role.
 *
 * The office app maps this to a URL. It used to keep its own literal
 * role → URL table beside a comment asking the reader to keep it in sync with
 * this file; a role added there and forgotten here left that user on a blank
 * office app, with nothing failing (AUTH-08).
 */
export const portalKindForRole = (
  slug: string | null | undefined,
): 'driver' | 'contractor' | null => {
  if (slug == null) return null
  if (DRIVER_PORTAL_ROLE_SET.has(slug)) return 'driver'
  if (CONTRACTOR_PORTAL_ROLE_SET.has(slug)) return 'contractor'
  return null
}

const PORTAL_ONLY_ROLE_SET = new Set<string>(PORTAL_ONLY_ROLE_SLUGS)

// True when a role slug belongs to a dedicated portal app and so must be kept
// out of an office/admin session.
export const isPortalOnlyRole = (slug: string | null | undefined): boolean =>
  PORTAL_ONLY_ROLE_SET.has(slug ?? '')
